// Extracts a filing date from an MCA-style filename. MCA portal exports and
// intermediary tools use many ad-hoc formats — we try the most specific
// patterns first and fall back to FY / year-only matches.

export type DateConfidence = "exact" | "fy" | "year-only";

export type ExtractedDate = {
  date: Date;
  confidence: DateConfidence;
};

const MONTHS: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const ISO_RE =
  /(?<!\d)(20\d{2})[-_./](0[1-9]|1[0-2])[-_./](0[1-9]|[12]\d|3[01])(?!\d)/;
const DMY_RE =
  /(?<!\d)(0[1-9]|[12]\d|3[01])[-_./](0[1-9]|1[0-2])[-_./](20\d{2})(?!\d)/;
const DMMMY_RE =
  /(?<!\d)(0?[1-9]|[12]\d|3[01])[-_ ]([A-Za-z]{3,4})[-_ ](20\d{2})(?!\d)/;
const DDMMYYYY_RE =
  /(?<!\d)(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(20\d{2})(?!\d)/;
const FY_RE = /FY[\s_-]*(?:20)?(\d{2})[-_/](?:20)?(\d{2})/i;
const YEAR_RE = /(?<!\d)(20\d{2})(?!\d)/;

function safeDate(y: number, m: number, d: number): Date | null {
  const dt = new Date(Date.UTC(y, m, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== m ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return dt;
}

export function extractFileDate(filename: string): ExtractedDate | null {
  const base = filename.split("/").pop() ?? filename;

  let m = base.match(ISO_RE);
  if (m) {
    const d = safeDate(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (d) return { date: d, confidence: "exact" };
  }

  m = base.match(DMY_RE);
  if (m) {
    const d = safeDate(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    if (d) return { date: d, confidence: "exact" };
  }

  m = base.match(DMMMY_RE);
  if (m) {
    const month = MONTHS[m[2].toLowerCase()];
    if (month !== undefined) {
      const d = safeDate(Number(m[3]), month, Number(m[1]));
      if (d) return { date: d, confidence: "exact" };
    }
  }

  m = base.match(DDMMYYYY_RE);
  if (m) {
    const d = safeDate(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    if (d) return { date: d, confidence: "exact" };
  }

  m = base.match(FY_RE);
  if (m) {
    // FY 22-23 ends 31-Mar-2023; end year wins.
    const endYear = 2000 + Number(m[2]);
    const d = safeDate(endYear, 2, 31);
    if (d) return { date: d, confidence: "fy" };
  }

  m = base.match(YEAR_RE);
  if (m) {
    const d = safeDate(Number(m[1]), 11, 31);
    if (d) return { date: d, confidence: "year-only" };
  }

  return null;
}

// Machine-readable YYYY-MM-DD (UTC) for sending to the backend. Returns null
// when no date could be extracted. Note that "fy"/"year-only" confidence dates
// are placeholders (31-Mar / 31-Dec) — see extractFileDate.
export function toISODate(extracted: ExtractedDate | null): string | null {
  if (!extracted) return null;
  const { date } = extracted;
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function formatExtractedDate(extracted: ExtractedDate | null): string {
  if (!extracted) return "—";
  const { date, confidence } = extracted;
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mmm = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const yyyy = date.getUTCFullYear();
  if (confidence === "year-only") return String(yyyy);
  if (confidence === "fy") return `FY ${yyyy - 1}-${String(yyyy).slice(2)}`;
  return `${dd}-${mmm}-${yyyy}`;
}
